import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, Bell, LogOut, Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Input } from "../../../ui/input";
import { Button } from "../../../ui/button";
import { LOGIN_CONSTANTS } from "../../../sharedfiles/Constants";
import { logoutApi } from "../../../sharedfiles/EndpointConfig";
import ThemeToggle from "../ThemeToggle/ThemeToggle";

const Header = ({
  // open,
  // handleSidebarClick,
  notifications,
  biReportURLs,
  formatReportFileName,
  downloadFile,
  getOriginalReportKey,
  nToggler,
  setNToggler,
  nRef,
  navigateToDestination,
  routeCheck,
  brandData,
  brand,
  brandHandler,
  handleLogOut,
  // isAffiliateUser,
  // starbetLogoPermission,
  // betVitaLogo,
  // betCorrectLogo,
  // sokaBetLogo,
  // topSahaLogo,
  // NElogo,
  // starbetLogo,
  // BetVitaLogo,
  // BetCoreectLogo,
  // SokabetLogo,
  // TopSahaLogo,
}) => {
  const totalNotificationCount =
    (notifications?.totalCount || 0) +
    Number(Object.keys(biReportURLs || {}).length);

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [bellAnimate, setBellAnimate] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const prevCountRef = useRef(totalNotificationCount);

  // Show an in-dropdown search only when there are more than 10 brands.
  const showBrandSearch = (brandData?.length || 0) > 10;
  const filteredBrandData =
    showBrandSearch && brandSearch.trim()
      ? brandData.filter((item) =>
        item?.name?.toLowerCase().includes(brandSearch.trim().toLowerCase())
      )
      : brandData;

  useEffect(() => {
    if (totalNotificationCount > 0 && totalNotificationCount !== prevCountRef.current) {
      setBellAnimate(false);
      // Trigger reflow so re-adding the class restarts animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setBellAnimate(true));
      });
      const timer = setTimeout(() => setBellAnimate(false), 1700);
      prevCountRef.current = totalNotificationCount;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = totalNotificationCount;
  }, [totalNotificationCount]);

  const onLogout = async (e) => {
    e.preventDefault();
    setIsLoggingOut(true);
    const token = localStorage.getItem("token");
    const merchantId = localStorage.getItem("merchantkey") || localStorage.getItem("merchantId");
    try {
      await fetch(logoutApi.logout, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorisation: token, MerchantId: merchantId },
      });
    } catch (_) { }
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center px-4 gap-4">
        <div className="flex items-center gap-3 ml-auto">

          {/* 🔔 Notifications */}
          <div className="relative" ref={nRef}>
            <button
              onClick={() => setNToggler(!nToggler)}
              className="relative p-2 rounded-md hover:bg-accent transition-colors"
            >
              <Bell size={24} className={`cursor-pointer transition-colors${bellAnimate ? " bell-ring text-destructive" : ""}`} />

              {totalNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[11px] font-normal">
                  {totalNotificationCount > 99 ? "99+" : totalNotificationCount}
                </span>
              )}
            </button>

            {/* Dropdown */}
            <div
              className={`absolute right-0 mt-2 w-80 rounded-lg border bg-popover text-popover-foreground shadow-lg transition-all ${nToggler ? "opacity-100 visible" : "opacity-0 invisible"
                }`}
            >
              <div className="p-4 border-b font-semibold">
                Notifications
              </div>

              <div className="max-h-96 overflow-y-auto divide-y">

                {/* Deposit */}
                {notifications?.pendingDepositCount > 0 && (
                  <Link
                    to="/pendingdeposits"
                    className="block p-4 hover:bg-accent transition-colors text-sm"
                    onClick={() => navigateToDestination("deposits")}
                  >
                    You have{" "}
                    <span className="font-semibold text-primary">
                      {notifications?.pendingDepositCount}
                    </span>{" "}
                    pending deposits
                  </Link>
                )}

                {/* Withdrawals */}
                {notifications?.pendingWithdrawals > 0 && (
                  <Link
                    to={routeCheck("withdrawals")}
                    className="block p-4 hover:bg-accent transition-colors text-sm"
                    onClick={() =>
                      navigateToDestination("withdrawals")
                    }
                  >
                    You have{" "}
                    <span className="font-semibold text-primary">
                      {notifications?.pendingWithdrawals}
                    </span>{" "}
                    pending withdrawals
                  </Link>
                )}

                {/* Affiliate */}
                {notifications?.pendingAffiliateUserRequestCount > 0 && (
                  <Link
                    to={routeCheck("becomeaffiliate")}
                    className="block p-4 hover:bg-accent transition-colors text-sm"
                    onClick={() =>
                      navigateToDestination("becomeaffiliate")
                    }
                  >
                    You have{" "}
                    <span className="font-semibold text-primary">
                      {notifications?.pendingAffiliateUserRequestCount}
                    </span>{" "}
                    pending Affiliate Requests
                  </Link>
                )}

                {/* 📊 BI Reports */}
                {biReportURLs &&
                  Object.entries(biReportURLs)
                    .sort((a, b) => {
                      const extractDateTimeNumber = (fileName) => {
                        const match = fileName.match(/\d{8}_\d+$/);
                        return match
                          ? Number(match[0].replace("_", ""))  // 20260212145823
                          : 0;
                      };

                      const dateTimeA = extractDateTimeNumber(a[0]);
                      const dateTimeB = extractDateTimeNumber(b[0]);

                      // 🔥 Newest first
                      return dateTimeB - dateTimeA;
                    })
                    .map(([fileName, url]) => {
                      const cleanName = formatReportFileName(fileName);

                      return (
                        <div
                          key={fileName}
                          className="block p-4 hover:bg-accent transition-colors text-sm cursor-pointer"
                          onClick={() => downloadFile(url, getOriginalReportKey(fileName))}
                          style={{ cursor: "pointer" }}
                        >
                          <div style={{ fontSize: "15px" }}>{cleanName}</div>
                        </div>
                      );
                    })}

                {/* No Data */}
                {totalNotificationCount === 0 && (
                  <div className="p-4 text-sm text-muted-foreground text-center">
                    No notifications found
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 🌗 Theme Toggle */}
          <ThemeToggle />

          {/* Brand Selector */}
          <div className="w-48">
            <Select
              value={
                localStorage.getItem("merchantkey")
                  ? localStorage.getItem("merchantkey")
                  : brand ||
                  (brandData &&
                    brandData[0]?.merchantKey)
              }
              onValueChange={(value) => {
                const event = { target: { value } };
                brandHandler(event);
              }}
              onOpenChange={(open) => {
                if (!open) setBrandSearch("");
              }}
            >
              <SelectTrigger id="selectBrand">
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent
                header={
                  showBrandSearch ? (
                    <div className="border-b bg-popover p-2">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          autoFocus
                          placeholder="Search brand..."
                          value={brandSearch}
                          onChange={(e) => setBrandSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="h-8 pl-8"
                        />
                      </div>
                    </div>
                  ) : null
                }
              >
                {filteredBrandData?.length ? (
                  filteredBrandData.map((item, index) => {
                    const displayName =
                      item.name === "starpunter"
                        ? "StarPunter"
                        : item.name.charAt(0).toUpperCase() +
                        item.name.slice(1);
                    return (
                      <SelectItem
                        key={item.merchantKey ?? index}
                        value={item.merchantKey}
                      >
                        {displayName}
                      </SelectItem>
                    );
                  })
                ) : (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    No brands found
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Logout */}
          <a href="/login" onClick={onLogout}>
            <Button
              variant="destructive"
              size="default"
              className="inline-flex items-center gap-2"
              id="logout"
              disabled={isLoggingOut}
            >
              {isLoggingOut ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  <span className="hidden sm:inline">Logging out...</span>
                </>
              ) : (
                <>
                  <LogOut size={18} />
                  <span className="hidden sm:inline">
                    {LOGIN_CONSTANTS.logout_btn}
                  </span>
                </>
              )}
            </Button>
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;